import { lookupBonoNombreById } from '@core/infrastructure/driven/sqljs/sqlJsLookupBonoNombre.ts';
import {
  captureTipoBonoTextFromSqlRow,
  lookupTipoBonoNombreById,
  participanteTipoBonoCatalogId,
} from '@core/infrastructure/driven/sqljs/sqlJsLookupTipoBonoNombre.ts';
import type { Database } from 'sql.js';
import {
  publicFileUrl,
  queryOne,
  rowFirstKeyColumn as pepCol,
  sqlPositiveIntId,
  type SqlJsBind,
} from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import type {
  ParticipanteBusquedaRedencionPrefill,
  ParticipanteResumen,
} from '../../../shared/participante/domain/participanteLookupTypes.ts';
import type { ParticipanteBusquedaPrefillResolve } from '../../../shared/participante/domain/participanteBusquedaPrefillResolve.ts';
import { lookupParticipanteSqlRowByDocumentoOrNombre } from '../../../shared/participante/infrastructure/findParticipanteSqlByDocumentoOrNombre.ts';
import { mapParticipanteSqlRowToResumen } from '../../../shared/participante/infrastructure/mapParticipanteSqlRowToResumen.ts';
import { listParticipanteSqlRowsByDocumentoOrNombre } from '../../../shared/participante/infrastructure/findParticipanteSqlByDocumentoOrNombre.ts';
import { mapParticipanteSqlRowToBusquedaCandidate } from '../../../shared/participante/infrastructure/mapParticipanteSqlRowToBusquedaCandidate.ts';
import type { ParticipanteBusquedaCandidate } from '../../../shared/participante/domain/participanteBusquedaCandidate.ts';

type SqlBind = SqlJsBind;

/** Resolve guardian row tied to PEP (same rules as persistence / PE module). */
export function resolveAcudienteRowForPrefill(
  db: Database,
  pep: Record<string, SqlBind>,
): Record<string, SqlBind> | null {
  const esAcud = Number(pepCol(pep, 'es_acudiente', 'ES_ACUDIENTE') ?? 0) === 1;
  if (!esAcud) return null;

  const aid = sqlPositiveIntId(pepCol(pep, 'acudiente_id', 'ACUDIENTE_ID'));
  if (aid != null) {
    const row = queryOne(db, 'SELECT * FROM acudientes WHERE id = ?', [aid]);
    if (row) return row;
  }

  const pid = sqlPositiveIntId(pepCol(pep, 'participante_id', 'PARTICIPANTE_ID'));
  if (pid == null) return null;
  return queryOne(db, 'SELECT * FROM acudientes WHERE participante_id = ? ORDER BY id DESC LIMIT 1', [
    pid,
  ]);
}

function buildRedencionParticipantePrefillFromResumen(
  db: Database,
  filesPublicPrefix: string,
  resumen: ParticipanteResumen,
): ParticipanteBusquedaRedencionPrefill {
  const catalogTipoBono = defaultTipoBonoTextForParticipante(db, resumen.id);
  const pep = queryOne(
    db,
    `SELECT * FROM punto_entrega_participante WHERE participante_id = ? ORDER BY id DESC LIMIT 1`,
    [resumen.id],
  );
  if (!pep) {
    const rbBonoId = lastBonoIdFromRedencionBonoParticipante(db, resumen.id);
    const rbTipoBono = lastTipoBonoTextFromRedencionBonoParticipante(db, resumen.id);
    return {
      resumen,
      desdeUltimoPuntoEntrega: false,
      nombreParticipante: resumen.nombre,
      telefono: resumen.telefono,
      celular: resumen.celular,
      personaAutorizada: false,
      nombrePersonaAutorizada: '',
      asistencia: false,
      acta: '',
      entregaBolsa: false,
      bonoId: rbBonoId,
      bonoNombre: lookupBonoNombreById(db, rbBonoId),
      tipoBono: rbTipoBono.length > 0 ? rbTipoBono : catalogTipoBono,
      proveedorId: null,
      grupoAlimentosId: null,
      firmaBase64: '',
      observaciones: '',
      registroFotografico: '',
      politicaTratamientos: resumen.aceptaTerminos,
    };
  }
  const esAcud = Number(pepCol(pep, 'es_acudiente', 'ES_ACUDIENTE') ?? 0) === 1;
  let nombrePersonaAutorizada = '';
  if (esAcud) {
    const acudienteRow = resolveAcudienteRowForPrefill(db, pep);
    if (acudienteRow) {
      nombrePersonaAutorizada = String(acudienteRow.nombre ?? '').trim();
    }
  }
  let tipoBono = captureTipoBonoTextFromSqlRow(db, pep);
  if (!tipoBono) {
    tipoBono = lastTipoBonoTextFromRedencionBonoParticipante(db, resumen.id);
  }
  if (!tipoBono) {
    tipoBono = catalogTipoBono;
  }

  let bonoId = sqlPositiveIntId(pepCol(pep, 'bono_id', 'BONO_ID'));
  bonoId ??= lastBonoIdFromRedencionBonoParticipante(db, resumen.id);

  return {
    resumen,
    desdeUltimoPuntoEntrega: true,
    nombreParticipante: resumen.nombre,
    telefono: resumen.telefono,
    celular: resumen.celular,
    personaAutorizada: esAcud,
    nombrePersonaAutorizada,
    asistencia: Number(pepCol(pep, 'asistencia', 'ASISTENCIA') ?? 0) === 1,
    acta: publicFileUrl(pepCol(pep, 'acta', 'ACTA') ?? '', filesPublicPrefix),
    entregaBolsa: Number(pepCol(pep, 'entrega_bolsa', 'ENTREGA_BOLSA') ?? 0) === 1,
    bonoId,
    bonoNombre: lookupBonoNombreById(db, bonoId),
    tipoBono,
    proveedorId: sqlPositiveIntId(pepCol(pep, 'proveedor_id', 'PROVEEDOR_ID')),
    grupoAlimentosId: sqlPositiveIntId(pepCol(pep, 'grupo_alimentos_id', 'GRUPO_ALIMENTOS_ID')),
    firmaBase64: publicFileUrl(pepCol(pep, 'firma_base64', 'FIRMA_BASE64') ?? '', filesPublicPrefix),
    observaciones: String(pepCol(pep, 'observaciones', 'OBSERVACIONES') ?? ''),
    registroFotografico: publicFileUrl(
      pepCol(pep, 'registro_fotografico', 'REGISTRO_FOTOGRAFICO') ?? '',
      filesPublicPrefix,
    ),
    politicaTratamientos: Number(pepCol(pep, 'politica_tratamientos', 'POLITICA_TRATAMIENTOS') ?? 0) === 1,
  };
}

export function resolveRedencionParticipantePrefillLookup(
  db: Database,
  filesPublicPrefix: string,
  documento: string,
): ParticipanteBusquedaPrefillResolve<ParticipanteBusquedaRedencionPrefill> {
  const lookup = lookupParticipanteSqlRowByDocumentoOrNombre(db, documento);
  if (lookup.status === 'found') {
    return {
      ok: true,
      prefill: buildRedencionParticipantePrefillFromResumen(
        db,
        filesPublicPrefix,
        mapParticipanteSqlRowToResumen(lookup.row),
      ),
    };
  }
  return { ok: false, reason: lookup.status === 'empty' ? 'empty' : lookup.status };
}

export function resolveRedencionParticipantePrefillLookupByParticipanteId(
  db: Database,
  filesPublicPrefix: string,
  participanteId: number,
): ParticipanteBusquedaPrefillResolve<ParticipanteBusquedaRedencionPrefill> {
  const row = queryOne(
    db,
    `SELECT p.*, c.nombre AS comuna_nombre FROM participantes p
     LEFT JOIN comunas c ON c.id = p.comuna_id
     WHERE p.id = ? AND p.estado = 1`,
    [participanteId],
  );
  if (!row) return { ok: false, reason: 'not_found' };
  return {
    ok: true,
    prefill: buildRedencionParticipantePrefillFromResumen(
      db,
      filesPublicPrefix,
      mapParticipanteSqlRowToResumen(row),
    ),
  };
}

export function listRedencionParticipanteBusquedaCandidates(
  db: Database,
  term: string,
): ParticipanteBusquedaCandidate[] {
  return listParticipanteSqlRowsByDocumentoOrNombre(db, term).map(mapParticipanteSqlRowToBusquedaCandidate);
}

/**
 * Participant search prefill for the redemption modal: participant catalog plus latest PEP, with redemption-line
 * fallback for voucher/type only when PEP does not specify them.
 */
export function resolveRedencionParticipantePrefillByDocumento(
  db: Database,
  filesPublicPrefix: string,
  documento: string,
): ParticipanteBusquedaRedencionPrefill | null {
  const resolved = resolveRedencionParticipantePrefillLookup(db, filesPublicPrefix, documento);
  return resolved.ok ? resolved.prefill : null;
}

function defaultTipoBonoTextForParticipante(db: Database, participanteId: number): string {
  return lookupTipoBonoNombreById(db, participanteTipoBonoCatalogId(db, participanteId)) ?? '';
}

export function lastTipoBonoTextFromRedencionBonoParticipante(db: Database, participanteId: number): string {
  if (!Number.isFinite(participanteId) || participanteId <= 0) return '';
  if (
    queryOne(db, `SELECT 1 FROM sqlite_master WHERE type='table' AND name='redencion_bono_participante'`, []) ==
    null
  ) {
    return '';
  }
  const row = queryOne(
    db,
    `SELECT tipo_bono FROM redencion_bono_participante
       WHERE participante_id = ?
       ORDER BY id DESC LIMIT 1`,
    [participanteId],
  );
  if (!row) return '';
  return captureTipoBonoTextFromSqlRow(db, row);
}

export function lastBonoIdFromRedencionBonoParticipante(db: Database, participanteId: number): number | null {
  if (!Number.isFinite(participanteId) || participanteId <= 0) return null;
  if (
    queryOne(db, `SELECT 1 FROM sqlite_master WHERE type='table' AND name='redencion_bono_participante'`, []) ==
    null
  ) {
    return null;
  }
  const row = queryOne(
    db,
    `SELECT rbp.bono_id AS bid FROM redencion_bono_participante rbp
       WHERE rbp.participante_id = ? AND rbp.bono_id IS NOT NULL AND rbp.bono_id > 0
       ORDER BY rbp.id DESC LIMIT 1`,
    [participanteId],
  );
  return sqlPositiveIntId(row?.bid);
}
